import { useEffect, useRef } from "react";
import {
  type Line, insertChord, mergeUp, moveChord, nearestChord, removeChord,
  setChord, splitAt, editText,
} from "../lib/pairededit";

export interface Focus {
  line: number;
  row: "text" | "chord";
  chord: number;
  caret: number;
}

/**
 * The chart as paired rows: chords above, words below.
 *
 * There is one caret position in the song and Tab moves between the two rows at
 * that position, rather than into a separate field. Correcting a chord is Tab,
 * type, Enter — three keys and no aiming, which matters because correcting a
 * chart is the same small move a few hundred times.
 */
export function PairedEditor({
  lines, focus, bars, disabled, onChange, onFocus,
}: {
  lines: Line[];
  focus: Focus;
  /** Bar number to show in the gutter, per line. */
  bars: (number | null)[];
  disabled?: boolean;
  onChange: (lines: Line[], focus?: Focus) => void;
  onFocus: (focus: Focus) => void;
}) {
  const texts = useRef<(HTMLInputElement | null)[]>([]);
  const chord = useRef<HTMLInputElement | null>(null);

  // Put the caret where the model says it is, after every structural change.
  useEffect(() => {
    if (disabled) return;
    if (focus.row === "text") {
      const field = texts.current[focus.line];
      if (field && document.activeElement !== field) field.focus();
      if (field) field.setSelectionRange(focus.caret, focus.caret);
    } else {
      chord.current?.focus();
      chord.current?.select();
    }
  }, [focus.line, focus.row, focus.chord, focus.caret, disabled]);

  /** A single contiguous edit, worked out from what changed. */
  function applyTyping(index: number, next: string, caret: number) {
    const line = lines[index];
    const old = line.text;
    let prefix = 0;
    while (prefix < old.length && prefix < next.length && old[prefix] === next[prefix]) prefix += 1;
    let suffix = 0;
    while (
      suffix < old.length - prefix && suffix < next.length - prefix
      && old[old.length - 1 - suffix] === next[next.length - 1 - suffix]
    ) suffix += 1;
    const edited = editText(line, prefix, old.length - suffix, next.slice(prefix, next.length - suffix));
    const copy = [...lines];
    copy[index] = edited;
    onChange(copy, { line: index, row: "text", chord: -1, caret });
  }

  function onTextKey(event: React.KeyboardEvent<HTMLInputElement>, index: number) {
    const field = event.currentTarget;
    const caret = field.selectionStart ?? 0;
    const line = lines[index];

    if (event.key === "Enter") {
      event.preventDefault();
      onChange(splitAt(lines, index, caret),
               { line: index + 1, row: "text", chord: -1, caret: 0 });
      return;
    }
    if (event.key === "Backspace" && caret === 0 && field.selectionEnd === 0) {
      if (index === 0) return;
      event.preventDefault();
      const merged = mergeUp(lines, index);
      onChange(merged.lines,
               { line: index - 1, row: "text", chord: -1, caret: merged.caret });
      return;
    }
    if (
      event.key === "Delete"
      && caret === line.text.length
      && field.selectionEnd === line.text.length
      && index < lines.length - 1
    ) {
      // Forward delete at the end of a line pulls the next line up onto it —
      // the same join as Backspace at column 0, from the other side of the
      // seam. The caret stays exactly where it was, so the joined text arrives
      // to the right of it.
      event.preventDefault();
      const merged = mergeUp(lines, index + 1);
      if (merged.lines !== lines) {
        onChange(merged.lines,
                 { line: index, row: "text", chord: -1, caret: line.text.length });
      }
      return;
    }
    if (event.key === "Tab" && !event.shiftKey) {
      event.preventDefault();
      let which = nearestChord(line, caret);
      if (which < 0) {
        const added = insertChord(line, caret);
        const copy = [...lines];
        copy[index] = added.line;
        onChange(copy, { line: index, row: "chord", chord: added.which, caret });
        return;
      }
      onFocus({ line: index, row: "chord", chord: which, caret });
      return;
    }
    if ((event.key === "k" || event.key === "K") && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      const added = insertChord(line, caret);
      const copy = [...lines];
      copy[index] = added.line;
      onChange(copy, { line: index, row: "chord", chord: added.which, caret });
      return;
    }
    if (event.key === "ArrowUp" && index > 0) {
      event.preventDefault();
      onFocus({ line: index - 1, row: "text", chord: -1,
                caret: Math.min(caret, lines[index - 1].text.length) });
      return;
    }
    if (event.key === "ArrowDown" && index < lines.length - 1) {
      event.preventDefault();
      onFocus({ line: index + 1, row: "text", chord: -1,
                caret: Math.min(caret, lines[index + 1].text.length) });
    }
  }

  function onChordKey(event: React.KeyboardEvent<HTMLInputElement>) {
    const line = lines[focus.line];
    const copy = [...lines];

    if (event.key === "Escape" || event.key === "Enter") {
      event.preventDefault();
      onFocus({ ...focus, row: "text", chord: -1 });
      return;
    }
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      copy[focus.line] = moveChord(line, focus.chord, event.key === "ArrowLeft" ? -1 : 1);
      onChange(copy, focus);
      return;
    }
    if (event.key === "Tab") {
      event.preventDefault();
      const next = focus.chord + (event.shiftKey ? -1 : 1);
      if (next < 0 || next >= line.chords.length) {
        onFocus({ ...focus, row: "text", chord: -1 });
        return;
      }
      onFocus({ ...focus, chord: next, caret: line.chords[next].at });
      return;
    }
    if (event.key === "Backspace" && !event.currentTarget.value) {
      event.preventDefault();
      copy[focus.line] = removeChord(line, focus.chord);
      onChange(copy, { ...focus, row: "text", chord: -1 });
    }
  }

  return (
    <div className="paired">
      {lines.map((line, index) => {
        const active = focus.line === index;
        return (
          <div key={index} className={`pair${active ? " focus" : ""}`}>
            <div className="pair-gutter mono">{bars[index] ?? ""}</div>
            <div className="pair-rows">
              <div className="pair-chords mono">
                {line.chords.map((item, which) => (
                  active && focus.row === "chord" && focus.chord === which ? (
                    <input
                      key={which}
                      ref={chord}
                      className="pair-chord-input mono"
                      style={{ left: `${item.at}ch` }}
                      value={item.name}
                      disabled={disabled}
                      onChange={(event) => {
                        const copy = [...lines];
                        copy[index] = setChord(line, which, event.target.value);
                        onChange(copy, focus);
                      }}
                      onKeyDown={onChordKey}
                      onBlur={() => onFocus({ ...focus, row: "text", chord: -1 })}
                    />
                  ) : (
                    <button
                      key={which}
                      type="button"
                      className="pair-chord"
                      style={{ left: `${item.at}ch` }}
                      disabled={disabled}
                      onClick={() => onFocus({
                        line: index, row: "chord", chord: which, caret: item.at,
                      })}
                    >{item.name}</button>
                  )
                ))}
              </div>

              {line.instrumental ? (
                <div className="pair-marker mono">[ instrumental ]</div>
              ) : (
                <input
                  ref={(element) => { texts.current[index] = element; }}
                  className="pair-text mono"
                  value={line.text}
                  disabled={disabled}
                  spellCheck={false}
                  onChange={(event) =>
                    applyTyping(index, event.target.value, event.target.selectionStart ?? 0)}
                  onKeyDown={(event) => onTextKey(event, index)}
                  onClick={(event) => onFocus({
                    line: index, row: "text", chord: -1,
                    caret: event.currentTarget.selectionStart ?? 0,
                  })}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
